// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatSort, MatSortModule } from '@angular/material/sort';
import { ButtonsComponent } from "../../../shared/buttons/buttons.component";

export type HceAnnotationModalData = {
  annotationFiles: string[];
  selectedFiles: string[];
  annotUsedInFilter: string[];
}

type Row = {
  filename: string;
  selected: boolean;
  isUsedInFilter: boolean;
}

@Component({
  selector: 'app-hce-annotation-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatTableModule,
    MatCheckboxModule,
    MatSortModule,
    ButtonsComponent,
  ],
  templateUrl: './hce-annotation-modal.component.html',
  styleUrls: ['./hce-annotation-modal.component.css'],
})
export class HceAnnotationModalComponent implements OnInit, AfterViewInit {
  displayedColumns: string[] = ['filename'];
  dataSource = new MatTableDataSource<Row>([]);
  isLoaded = false;

  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    public dialogRef: MatDialogRef<HceAnnotationModalComponent, string[] | undefined>,
    @Inject(MAT_DIALOG_DATA) public data: HceAnnotationModalData,
  ) {}

  ngOnInit(): void {
    const selectedSet = new Set(this.data.selectedFiles);
    const usedInFilterSet = new Set(this.data.annotUsedInFilter);
    const rows: Row[] = this.data.annotationFiles.map(filename => ({
      filename,
      selected: selectedSet.has(filename),
      isUsedInFilter: usedInFilterSet.has(filename)
    }));
    this.dataSource.data = rows;
    this.isLoaded = true;
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  get selectedFiles(): string[] {
    return this.dataSource.data
      .filter(r => r.selected)
      .map(r => r.filename);
  }

  get isDisabled(): boolean {
    const current = this.selectedFiles;
    const prev = this.data.selectedFiles;
    if (current.length !== prev.length) return false;
    const set = new Set(prev);
    for (const f of current) {
      if (!set.has(f)) return false;
    }
    return true;
  }

  onChange(selected: boolean, row: Row): void {
    if(row.isUsedInFilter) return;
    row.selected = selected;
    this.dataSource.data = [...this.dataSource.data];
  }

  onOkClick(): void {
    this.dialogRef.close(this.selectedFiles);
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }

  trackByFilename = (_: number, row: Row) => row.filename;
}
